import api from '@/lib/axios';
import type { ListParams, Paginated } from '@/services/school.api';

function unwrap<T>(r: { data: { data?: T } | T }): T {
  const body: any = r.data;
  return (body?.data ?? body) as T;
}

export const LMS_COURSE_STATUSES = ['draft', 'published', 'archived'] as const;
export type LmsCourseStatus = (typeof LMS_COURSE_STATUSES)[number];

export const LMS_LESSON_TYPES = ['video', 'document', 'text', 'link', 'live'] as const;
export type LmsLessonType = (typeof LMS_LESSON_TYPES)[number];

export interface LmsCourse {
  id: string;
  schoolId: string;
  title: string;
  description: string | null;
  coverUrl: string | null;
  subject: string | null;
  status: LmsCourseStatus;
  createdByUserId: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export interface LmsModule {
  id: string;
  schoolId: string;
  courseId: string;
  title: string;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  lessonCount: number;
}

export interface LmsLessonVideoContent {
  url: string;
}
export interface LmsLessonDocumentContent {
  url: string;
}
export interface LmsLessonLinkContent {
  url: string;
}
export interface LmsLessonTextContent {
  body: string;
}
export interface LmsLessonLiveContent {
  url: string;
  scheduledAt: string;
}
export type LmsLessonContent =
  | LmsLessonVideoContent
  | LmsLessonDocumentContent
  | LmsLessonLinkContent
  | LmsLessonTextContent
  | LmsLessonLiveContent;

export interface LmsLesson {
  id: string;
  schoolId: string;
  moduleId: string;
  type: LmsLessonType;
  title: string;
  content: LmsLessonContent;
  isMandatory: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export interface LmsCourseListParams extends ListParams {
  status?: LmsCourseStatus;
}

export interface CreateLmsCoursePayload {
  title: string;
  description?: string;
  coverUrl?: string;
  subject?: string;
}
export type UpdateLmsCoursePayload = Partial<CreateLmsCoursePayload>;

export interface CreateLmsLessonPayload {
  type: LmsLessonType;
  title: string;
  content: LmsLessonContent;
  isMandatory?: boolean;
}
export interface UpdateLmsLessonPayload {
  title?: string;
  content?: LmsLessonContent;
  isMandatory?: boolean;
}

export const LmsCoursesApi = {
  list: async (params?: LmsCourseListParams): Promise<Paginated<LmsCourse>> =>
    unwrap(await api.get('/school/lms/courses', { params })),
  get: async (id: string): Promise<LmsCourse> => unwrap(await api.get(`/school/lms/courses/${id}`)),
  create: async (data: CreateLmsCoursePayload): Promise<LmsCourse> =>
    unwrap(await api.post('/school/lms/courses', data)),
  update: async (id: string, data: UpdateLmsCoursePayload): Promise<LmsCourse> =>
    unwrap(await api.patch(`/school/lms/courses/${id}`, data)),
  remove: async (id: string): Promise<{ deleted: boolean; id: string }> =>
    unwrap(await api.delete(`/school/lms/courses/${id}`)),
  publish: async (id: string): Promise<LmsCourse> => unwrap(await api.post(`/school/lms/courses/${id}/publish`, {})),
  archive: async (id: string): Promise<LmsCourse> => unwrap(await api.post(`/school/lms/courses/${id}/archive`, {})),
};

export const LmsModulesApi = {
  list: async (courseId: string): Promise<LmsModule[]> =>
    unwrap(await api.get(`/school/lms/courses/${courseId}/modules`)),
  create: async (courseId: string, title: string): Promise<LmsModule> =>
    unwrap(await api.post(`/school/lms/courses/${courseId}/modules`, { title })),
  update: async (courseId: string, moduleId: string, title: string): Promise<LmsModule> =>
    unwrap(await api.patch(`/school/lms/courses/${courseId}/modules/${moduleId}`, { title })),
  remove: async (
    courseId: string,
    moduleId: string,
  ): Promise<{ deleted: boolean; id: string; lessonsDeleted: number }> =>
    unwrap(await api.delete(`/school/lms/courses/${courseId}/modules/${moduleId}`)),
  reorder: async (courseId: string, orderedModuleIds: string[]): Promise<LmsModule[]> =>
    unwrap(await api.post(`/school/lms/courses/${courseId}/modules/reorder`, { orderedModuleIds })),
};

export const LmsLessonsApi = {
  list: async (moduleId: string): Promise<LmsLesson[]> => unwrap(await api.get(`/school/lms/modules/${moduleId}/lessons`)),
  create: async (moduleId: string, data: CreateLmsLessonPayload): Promise<LmsLesson> =>
    unwrap(await api.post(`/school/lms/modules/${moduleId}/lessons`, data)),
  update: async (moduleId: string, lessonId: string, data: UpdateLmsLessonPayload): Promise<LmsLesson> =>
    unwrap(await api.patch(`/school/lms/modules/${moduleId}/lessons/${lessonId}`, data)),
  remove: async (moduleId: string, lessonId: string): Promise<{ deleted: boolean; id: string }> =>
    unwrap(await api.delete(`/school/lms/modules/${moduleId}/lessons/${lessonId}`)),
  reorder: async (moduleId: string, orderedLessonIds: string[]): Promise<LmsLesson[]> =>
    unwrap(await api.post(`/school/lms/modules/${moduleId}/lessons/reorder`, { orderedLessonIds })),
};

export interface LmsEnrollmentRow {
  id: string;
  studentId: string;
  admissionNumber: string | null;
  studentName: string | null;
  className: string | null;
  sectionName: string | null;
  progress: number;
  enrolledAt: string;
}

export interface BulkEnrollResult {
  enrolled: number;
  skipped: number;
  invalidIds: string[];
}

export const LmsEnrollmentsApi = {
  list: async (
    courseId: string,
    params?: { page?: number; limit?: number },
  ): Promise<Paginated<LmsEnrollmentRow>> =>
    unwrap(await api.get(`/school/lms/courses/${courseId}/enrollments`, { params })),
  bulkEnroll: async (courseId: string, studentIds: string[]): Promise<BulkEnrollResult> =>
    unwrap(await api.post(`/school/lms/courses/${courseId}/enrollments`, { studentIds })),
  remove: async (courseId: string, enrollmentId: string): Promise<{ deleted: boolean; id: string }> =>
    unwrap(await api.delete(`/school/lms/courses/${courseId}/enrollments/${enrollmentId}`)),
};

export const LMS_ASSIGNMENT_STATUSES = ['draft', 'published'] as const;
export type LmsAssignmentStatus = (typeof LMS_ASSIGNMENT_STATUSES)[number];

export const LMS_SUBMISSION_TYPES = ['text', 'file', 'both'] as const;
export type LmsSubmissionType = (typeof LMS_SUBMISSION_TYPES)[number];

export interface LmsAssignment {
  id: string;
  schoolId: string;
  courseId: string;
  moduleId: string | null;
  title: string;
  instructions: string | null;
  maxMarks: number | null;
  dueAt: string | null;
  allowLate: boolean;
  submissionType: LmsSubmissionType;
  status: LmsAssignmentStatus;
  createdByUserId: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export interface LmsAssignmentListParams extends ListParams {
  status?: LmsAssignmentStatus;
}

export interface CreateLmsAssignmentPayload {
  title: string;
  instructions?: string;
  maxMarks?: number;
  dueAt?: string;
  allowLate?: boolean;
  submissionType?: LmsSubmissionType;
}
export type UpdateLmsAssignmentPayload = Partial<CreateLmsAssignmentPayload>;

export const LmsAssignmentsApi = {
  list: async (
    courseId: string,
    params?: LmsAssignmentListParams,
  ): Promise<Paginated<LmsAssignment>> =>
    unwrap(await api.get(`/school/lms/courses/${courseId}/assignments`, { params })),
  get: async (id: string): Promise<LmsAssignment> => unwrap(await api.get(`/school/lms/assignments/${id}`)),
  create: async (courseId: string, data: CreateLmsAssignmentPayload): Promise<LmsAssignment> =>
    unwrap(await api.post(`/school/lms/courses/${courseId}/assignments`, data)),
  update: async (id: string, data: UpdateLmsAssignmentPayload): Promise<LmsAssignment> =>
    unwrap(await api.patch(`/school/lms/assignments/${id}`, data)),
  remove: async (id: string): Promise<{ deleted: boolean; id: string }> =>
    unwrap(await api.delete(`/school/lms/assignments/${id}`)),
  publish: async (id: string): Promise<LmsAssignment> =>
    unwrap(await api.post(`/school/lms/assignments/${id}/publish`, {})),
};

export const LMS_SUBMISSION_STATUSES = ['submitted', 'graded', 'returned'] as const;
export type LmsSubmissionStatus = (typeof LMS_SUBMISSION_STATUSES)[number];

export interface LmsSubmissionFile {
  url: string;
  name: string;
  size: number;
  mimeType: string;
}

/** `textBody`/`files`/`feedback`/`gradedAt` are only present because the
 * backend's list endpoint was extended this step to forward fields it
 * already fetched internally — there is still no separate single-submission
 * GET route, so this list row IS the detail view's only data source. */
export interface LmsSubmissionRow {
  id: string;
  studentId: string | null;
  admissionNumber: string | null;
  studentName: string | null;
  submittedAt: string;
  isLate: boolean;
  status: LmsSubmissionStatus;
  marks: string | null;
  textBody: string | null;
  files: LmsSubmissionFile[];
  feedback: string | null;
  gradedAt: string | null;
}

export interface GradeSubmissionPayload {
  marks?: number;
  feedback?: string;
}

export interface ReturnSubmissionPayload {
  feedback: string;
}

export const LmsSubmissionsApi = {
  list: async (assignmentId: string, params?: ListParams): Promise<Paginated<LmsSubmissionRow>> =>
    unwrap(await api.get(`/school/lms/assignments/${assignmentId}/submissions`, { params })),
  grade: async (submissionId: string, data: GradeSubmissionPayload): Promise<LmsSubmissionRow> =>
    unwrap(await api.post(`/school/lms/submissions/${submissionId}/grade`, data)),
  returnForRevision: async (submissionId: string, data: ReturnSubmissionPayload): Promise<LmsSubmissionRow> =>
    unwrap(await api.post(`/school/lms/submissions/${submissionId}/return`, data)),
};

/** Mirrors school.api.ts's downloadExport mechanism (blob fetch through the
 * authenticated axios instance + object URL + synthetic anchor) — the only
 * authenticated-download convention in this codebase — parameterized with
 * the file's real name instead of a format-suffix. */
export async function downloadSubmissionFile(
  submissionId: string,
  index: number,
  filename: string,
): Promise<void> {
  const res = await api.get(`/school/lms/submissions/${submissionId}/files/${index}`, {
    responseType: 'blob',
  });
  const blob = new Blob([res.data]);
  const href = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = href;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(href);
}

export const LMS_QUIZ_STATUSES = ['draft', 'published'] as const;
export type LmsQuizStatus = (typeof LMS_QUIZ_STATUSES)[number];

export interface LmsQuizSettings {
  timeLimitMinutes?: number | null;
  attemptsAllowed?: number | null;
  shuffleQuestions?: boolean;
  shuffleOptions?: boolean;
  showResults?: 'immediately' | 'after_close';
  passMarkPercent?: number | null;
}

export interface LmsQuiz {
  id: string;
  schoolId: string;
  courseId: string;
  moduleId: string | null;
  title: string;
  description: string | null;
  settings: LmsQuizSettings;
  status: LmsQuizStatus;
  createdByUserId: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export type LmsQuizListItem = LmsQuiz & { questionCount: number; totalMarks: number };
export type LmsQuizDetail = LmsQuiz & { questions: LmsQuestion[] };

export interface LmsQuizListParams extends ListParams {
  status?: LmsQuizStatus;
}

export interface CreateLmsQuizPayload {
  title: string;
  moduleId?: string;
  description?: string;
  settings?: LmsQuizSettings;
}
export type UpdateLmsQuizPayload = Partial<CreateLmsQuizPayload>;

export const LmsQuizzesApi = {
  list: async (courseId: string, params?: LmsQuizListParams): Promise<Paginated<LmsQuizListItem>> =>
    unwrap(await api.get(`/school/lms/courses/${courseId}/quizzes`, { params })),
  get: async (id: string): Promise<LmsQuizDetail> => unwrap(await api.get(`/school/lms/quizzes/${id}`)),
  create: async (courseId: string, data: CreateLmsQuizPayload): Promise<LmsQuiz> =>
    unwrap(await api.post(`/school/lms/courses/${courseId}/quizzes`, data)),
  update: async (id: string, data: UpdateLmsQuizPayload): Promise<LmsQuiz> =>
    unwrap(await api.patch(`/school/lms/quizzes/${id}`, data)),
  remove: async (id: string): Promise<{ deleted: boolean; id: string; questionsDeleted: number }> =>
    unwrap(await api.delete(`/school/lms/quizzes/${id}`)),
  publish: async (id: string): Promise<LmsQuiz> => unwrap(await api.post(`/school/lms/quizzes/${id}/publish`, {})),
  unpublish: async (id: string): Promise<LmsQuiz> =>
    unwrap(await api.post(`/school/lms/quizzes/${id}/unpublish`, {})),
};

export const LMS_QUESTION_TYPES = ['mcq_single', 'mcq_multi', 'true_false', 'short_answer'] as const;
export type LmsQuestionType = (typeof LMS_QUESTION_TYPES)[number];

export interface LmsQuestionOption {
  id: string;
  text: string;
}
export interface LmsMcqSingleContent {
  options: LmsQuestionOption[];
  correctOptionId: string;
}
export interface LmsMcqMultiContent {
  options: LmsQuestionOption[];
  correctOptionIds: string[];
}
export interface LmsTrueFalseContent {
  correctAnswer: boolean;
}
export interface LmsShortAnswerContent {
  expectedAnswer?: string;
  caseSensitive?: boolean;
}
export type LmsQuestionContent =
  | LmsMcqSingleContent
  | LmsMcqMultiContent
  | LmsTrueFalseContent
  | LmsShortAnswerContent;

export interface LmsQuestion {
  id: string;
  schoolId: string;
  quizId: string;
  type: LmsQuestionType;
  prompt: string;
  content: LmsQuestionContent;
  marks: number;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export interface CreateLmsQuestionPayload {
  type: LmsQuestionType;
  prompt: string;
  content: LmsQuestionContent;
  marks: number;
}
export interface UpdateLmsQuestionPayload {
  prompt?: string;
  content?: LmsQuestionContent;
  marks?: number;
}

export const LmsQuestionsApi = {
  list: async (quizId: string): Promise<LmsQuestion[]> =>
    unwrap(await api.get(`/school/lms/quizzes/${quizId}/questions`)),
  create: async (quizId: string, data: CreateLmsQuestionPayload): Promise<LmsQuestion> =>
    unwrap(await api.post(`/school/lms/quizzes/${quizId}/questions`, data)),
  update: async (quizId: string, questionId: string, data: UpdateLmsQuestionPayload): Promise<LmsQuestion> =>
    unwrap(await api.patch(`/school/lms/quizzes/${quizId}/questions/${questionId}`, data)),
  remove: async (quizId: string, questionId: string): Promise<{ deleted: boolean; id: string }> =>
    unwrap(await api.delete(`/school/lms/quizzes/${quizId}/questions/${questionId}`)),
  reorder: async (quizId: string, orderedQuestionIds: string[]): Promise<LmsQuestion[]> =>
    unwrap(await api.post(`/school/lms/quizzes/${quizId}/questions/reorder`, { orderedQuestionIds })),
};

export const LMS_ATTEMPT_STATUSES = ['in_progress', 'submitted', 'expired', 'graded'] as const;
export type LmsAttemptStatus = (typeof LMS_ATTEMPT_STATUSES)[number];

export interface LmsQuizAttemptRow {
  id: string;
  studentId: string | null;
  admissionNumber: string | null;
  studentName: string | null;
  attemptNumber: number;
  status: LmsAttemptStatus;
  score: string | null;
  maxScore: string;
  passed: boolean | null;
  startedAt: string;
  submittedAt: string | null;
  pendingManualCount: number;
}

export interface LmsQuizAttemptSnapshotQuestion {
  questionId: string;
  type: LmsQuestionType;
  prompt: string;
  content: LmsQuestionContent;
  marks: number;
  displayOrder: number;
}

export interface LmsAttemptAnswer {
  id: string;
  schoolId: string;
  attemptId: string;
  questionId: string;
  answer: Record<string, unknown>;
  isCorrect: boolean | null;
  marksAwarded: string | null;
  gradedByUserId: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Raw attempt entity fields + answers[] — exactly what GET /attempts/:id and
 * the grade endpoint both return; no studentName/admissionNumber here
 * (that enrichment only exists on the list response), so the attempts
 * modal keeps those from the list row it was opened from. */
export interface LmsQuizAttemptDetail {
  id: string;
  schoolId: string;
  quizId: string;
  enrollmentId: string;
  attemptNumber: number;
  status: LmsAttemptStatus;
  startedAt: string;
  submittedAt: string | null;
  expiresAt: string | null;
  questionsSnapshot: LmsQuizAttemptSnapshotQuestion[];
  score: string | null;
  maxScore: string;
  passed: boolean | null;
  gradedAt: string | null;
  answers: LmsAttemptAnswer[];
}

export interface GradeAttemptAnswerPayload {
  marksAwarded: number;
}

export const LmsQuizAttemptsApi = {
  list: async (quizId: string, params?: ListParams): Promise<Paginated<LmsQuizAttemptRow>> =>
    unwrap(await api.get(`/school/lms/quizzes/${quizId}/attempts`, { params })),
  get: async (attemptId: string): Promise<LmsQuizAttemptDetail> =>
    unwrap(await api.get(`/school/lms/attempts/${attemptId}`)),
  grade: async (
    attemptId: string,
    questionId: string,
    data: GradeAttemptAnswerPayload,
  ): Promise<LmsQuizAttemptDetail> =>
    unwrap(await api.post(`/school/lms/attempts/${attemptId}/answers/${questionId}/grade`, data)),
};

export interface LmsTeacherDashboardCourse {
  id: string;
  title: string;
  status: LmsCourseStatus;
  enrolledCount: number;
  avgProgress: number;
}

export interface LmsTeacherDashboardRecentSubmission {
  assignmentTitle: string | null;
  studentName: string | null;
  submittedAt: string;
  isLate: boolean;
}

export interface LmsTeacherDashboard {
  myCourses: LmsTeacherDashboardCourse[];
  pendingGrading: { assignmentSubmissions: number; shortAnswerQuestions: number };
  recentSubmissions: LmsTeacherDashboardRecentSubmission[];
}

export interface LmsAdminDashboardTotals {
  courses: { draft: number; published: number; archived: number };
  activeEnrollments: number;
  studentsEnrolled: number;
  completedEnrollments: number;
}

export interface LmsAdminDashboardCourseRow {
  id: string;
  title: string;
  status: LmsCourseStatus;
  enrolledCount: number;
  avgProgress: number;
  completionRate: number;
}

export interface LmsAdminDashboardActivity {
  type: 'enrollment' | 'completion' | 'submission';
  at: string;
  courseTitle: string | null;
  studentName: string | null;
  /** Only present (possibly null) when type === 'submission'. */
  assignmentTitle?: string | null;
}

export interface LmsAdminDashboard {
  totals: LmsAdminDashboardTotals;
  perCourse: Paginated<LmsAdminDashboardCourseRow>;
  recentActivity: LmsAdminDashboardActivity[];
}

export const LmsDashboardApi = {
  teacher: async (): Promise<LmsTeacherDashboard> => unwrap(await api.get('/school/lms/dashboard/teacher')),
  admin: async (params?: ListParams): Promise<LmsAdminDashboard> =>
    unwrap(await api.get('/school/lms/dashboard/admin', { params })),
};
